import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateModalsCloseDrawerModal } from "@src/reducers/your-reducer";
import { OtaBackupDrawerInterface } from "@src/types/ota-update";
import { useI18N } from "@src/language/language-context";
import { RootState } from "@src/reducers/store";
import { validateFields } from "@src/cloud-connect/utils/validate-fields";
import {
  errorSnackbar,
  successSnackbar,
  warningSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { postFirmwareBackupUseCase } from "@src/cloud-connect/ota-update/use-cases/post-firmware-backup-use-case";

type UseOtaFirmwareBackupDrawerModalProps = {
  inputValues: OtaBackupDrawerInterface;
  setInputValues: React.Dispatch<
    React.SetStateAction<OtaBackupDrawerInterface>
  >;
  fetchBackups: () => Promise<void>;
};

export const UseOtaFirmwareBackupDrawerModal = ({
  inputValues,
  setInputValues,
  fetchBackups,
}: UseOtaFirmwareBackupDrawerModalProps) => {
  const i18n = useI18N();
  const dispatch = useDispatch();

  const connectionView = useSelector(
    (state: RootState) => state.yourSlice?.connectionView,
  );

  const texts = {
    createFirmwareBackupText: i18n(
      "modules.otaFirmwareBackupDrawerModal.createFirmwareBackupText",
    ),
    backupDetailsText: i18n(
      "modules.otaFirmwareBackupDrawerModal.backupDetailsText",
    ),
    createBackupText: i18n(
      "modules.otaFirmwareBackupDrawerModal.createBackupText",
    ),
    cancelText: i18n("modules.otaFirmwareBackupDrawerModal.cancelText"),
    fixValidateFieldText: i18n(
      "modules.otaFirmwareBackupDrawerModal.fixValidateFieldText",
    ),
    firmwareVersionText: i18n(
      "modules.otaFirmwareBackupDrawerModal.firmwareVersionText",
    ),
    backupNameText: i18n("modules.otaFirmwareBackupDrawerModal.backupNameText"),
    backupNotesText: i18n(
      "modules.otaFirmwareBackupDrawerModal.backupNotesText",
    ),
    namePlaceholderText: i18n(
      "modules.otaFirmwareBackupDrawerModal.namePlaceholderText",
    ),
    notesPlaceholderText: i18n(
      "modules.otaFirmwareBackupDrawerModal.notesPlaceholderText",
    ),
    successMsg: i18n("modules.otaFirmwareBackupDrawerModal.successMsg"),
    errorMsg: i18n("modules.otaFirmwareBackupDrawerModal.errorMsg"),
    failedToCreateBackup: i18n(
      "modules.otaFirmwareBackupDrawerModal.failedToCreateBackup",
    ),
  };

  const handleCloseDrawerModal = useCallback((): void => {
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: false,
        id: "otaFirmwareBackupDrawerModal",
      }),
    );

    setInputValues({
      version: "",
      backup_name: "",
      backup_notes: "",
    });
  }, [dispatch, setInputValues]);

  const startBackupCreation = useCallback(async () => {
    try {
      const body = {
        type: "FIRMWAREBACKUP",
        filename: inputValues.backup_name,
        version: inputValues?.version || null,
        release_notes_intro: inputValues?.backup_notes || null,
        source_id: connectionView.source.source_id,
        release_notes_improvements: null,
        release_notes_fixes: null,
        release_notes_general: null,
      };

      const result = await postFirmwareBackupUseCase(body);

      if (result?.success && result.data) {
        handleCloseDrawerModal();
        successSnackbar(texts.successMsg, 5000);
        await fetchBackups();
      } else {
        errorSnackbar(texts.failedToCreateBackup, 5000);
      }
    } catch {
      errorSnackbar(texts.errorMsg, 5000);
    }
  }, [
    connectionView.source.source_id,
    fetchBackups,
    handleCloseDrawerModal,
    inputValues.backup_name,
    inputValues?.backup_notes,
    inputValues?.version,
    texts.errorMsg,
    texts.failedToCreateBackup,
    texts.successMsg,
  ]);

  const handleSubmit = useCallback(() => {
    const fieldsToValidate = [
      {
        key: "backup_name",
        label: texts.backupNameText,
      },
      {
        key: "version",
        label: texts.firmwareVersionText,
      },
    ];

    const emptyFields = validateFields(fieldsToValidate, inputValues);

    if (emptyFields.length > 0) {
      warningSnackbar(
        `${texts.fixValidateFieldText} ${emptyFields.join(", ")}.`,
        5000,
      );
      return;
    }

    startBackupCreation();
  }, [
    inputValues,
    startBackupCreation,
    texts.backupNameText,
    texts.fixValidateFieldText,
  ]);

  const handleBackupNameChange = (value: string) => {
    setInputValues((prev) => ({
      ...prev,
      backup_name: value,
    }));
  };

  const handleVersionChange = (value: string) => {
    setInputValues((prev) => ({
      ...prev,
      version: value,
    }));
  };

  const handleBackupNotesChange = (value: string) => {
    setInputValues((prev) => ({
      ...prev,
      backup_notes: value,
    }));
  };

  return {
    texts,
    handleCloseDrawerModal,
    handleSubmit,
    handleBackupNameChange,
    handleVersionChange,
    handleBackupNotesChange,
  };
};
